import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell } from 'recharts';
import { Loader2 } from 'lucide-react';
import PortalTooltip from './PortalTooltip';

const GrowthMomentumChart = ({ entities, trendData = [], isLoading, metricLabel = 'Metric', isPeerMode }) => {
  const [hoveredEntityId, setHoveredEntityId] = useState(null);

  // Derive momentum data from API trend data
  const { data, yDomain } = useMemo(() => {
    if (trendData.length < 2) return { data: [], yDomain: [0, 100] };

    let minGrowth = 0;
    let maxGrowth = 0;

    const processedData = trendData.slice(1).map((d, i) => {
      const point = { year: d.year, is_forecast: d.is_forecast };
      entities.forEach(entity => {
        const key = `entity_${entity.id}`;
        const prev = trendData[i][key];
        const curr = d[key];
        if (prev != null && curr != null && prev !== 0) {
          const growth = +(((curr - prev) / prev) * 100).toFixed(1);
          point[key] = growth;
          if (!d.is_forecast) {
            if (growth < minGrowth) minGrowth = growth;
            if (growth > maxGrowth) maxGrowth = growth;
          }
        } else {
          point[key] = null;
        }
      });
      return point;
    });

    const padding = 10;
    const domain = [
      Math.floor(minGrowth / 10) * 10 - padding,
      Math.ceil(maxGrowth / 10) * 10 + padding
    ];

    return { data: processedData, yDomain: domain };
  }, [trendData, entities]);

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="font-bold text-slate-800 mb-2">Year: {label}</p>
          {payload.map((entry, index) => {
            const entityId = entry.dataKey?.replace('entity_', '');
            const entity = entities.find(e => e.id == entityId);
            if (!entity) return null;
            const isHovered = hoveredEntityId == entityId;
            const isAnyHovered = hoveredEntityId !== null;
            const isBase = entity.isBaseEntity;
            
            let itemOpacity = 1;
            if (isPeerMode) {
              itemOpacity = (isBase || isHovered) ? 1 : 0.5;
            } else {
              itemOpacity = isAnyHovered ? (isHovered ? 1 : 0.5) : 1;
            }
            const shouldHighlight = isPeerMode ? (isBase || isHovered) : (isAnyHovered ? isHovered : false);
            
            const val = entry.value;
            const sign = val > 0 ? '+' : '';
            return (
              <div key={index} className="flex items-center justify-between gap-4 mb-1" style={{ opacity: itemOpacity }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span style={{ color: entry.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>{entry.name}</span>
                </div>
                <span className="font-medium" style={{ color: val >= 0 ? '#16a34a' : '#ef4444' }}>
                  {sign}{val}%
                </span>
              </div>
            );
          })}
        </PortalTooltip>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="relative flex flex-col items-center justify-center mb-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800">{metricLabel} YoY Growth</h3>
          <p className="text-xs text-slate-500">Year-over-Year Growth Rate (%)</p>
        </div>
      </div>
      
      <div className="flex-1 w-full min-h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {entities.map(entity => (
                <pattern key={`stripe-${entity.id}`} id={`stripe-${entity.id}`} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                  <line x1="0" y="0" x2="0" y2="8" stroke={entity.color} strokeWidth="4" opacity="0.5" />
                </pattern>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 12, fill: "#64748b" }} 
              width={45} 
              tickFormatter={(val) => `${val}%`}
              domain={yDomain}
              allowDataOverflow={true}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} 
              onMouseEnter={(e) => setHoveredEntityId(e.dataKey?.replace('entity_', ''))}
              onMouseLeave={() => setHoveredEntityId(null)}
            />
            <ReferenceLine y={0} stroke="#cbd5e1" />
            
            {entities.map(entity => {
              const isHovered = hoveredEntityId === entity.id;
              const isAnyHovered = hoveredEntityId !== null;
              
              let opacity = 1;
              let strokeWBase = 0;
              let strokeColor = "none";
              
              if (isPeerMode) {
                if (entity.isBaseEntity) {
                  opacity = 1;
                  strokeWBase = 1.5;
                  strokeColor = "#000";
                } else {
                  opacity = isHovered ? 1 : 0.25;
                  strokeWBase = isHovered ? 1.5 : 0;
                  strokeColor = isHovered ? "#000" : "none";
                }
              } else {
                opacity = 1;
                strokeWBase = 0;
                strokeColor = "none";
              }
              
              return (
              <Bar 
                key={entity.id} 
                dataKey={`entity_${entity.id}`} 
                name={entity.name} 
                maxBarSize={32}
                fill={entity.color}
                onMouseEnter={() => setHoveredEntityId(entity.id)}
                onMouseLeave={() => setHoveredEntityId(null)}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.is_forecast ? `url(#stripe-${entity.id})` : entity.color} 
                    fillOpacity={opacity}
                    stroke={entry.is_forecast ? entity.color : strokeColor}
                    strokeWidth={entry.is_forecast ? 1 : strokeWBase}
                  />
                ))}
              </Bar>
            )})}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GrowthMomentumChart;
